import type { AgentPresentationStep } from "@/lib/agent-presentation-types";
import { annotationSketchStrokes } from "@/lib/annotation-sketch";
import {
  presentCursor,
  type PresentAnnotation,
  type PresentCursorRequest,
} from "@/lib/canvas-cursor-presentation";
import { drawDurationMs } from "@/lib/presentation-draw-path";
import { animatePresentationPath } from "@/lib/presentation-path-animation";
import { sketchMarkTip } from "@/lib/sketch-stroke/sketch-mark";
import { prefersReducedMotion } from "@/lib/reveal-motion";

type HighlightStep = Extract<AgentPresentationStep, { kind: "highlight" }>;

function highlightStrokes(annotation: PresentAnnotation, annotationId: string) {
  return annotationSketchStrokes({
    ...annotation,
    globalX: annotation.x,
    globalY: annotation.y,
    annotationId,
  });
}

/** How long the pen takes to draw a closed highlight. Paths keep their
 * endpoint anchor and appear at once. */
export function highlightDrawingDuration(
  annotation: PresentAnnotation,
  step: HighlightStep,
  annotationId: string,
): number {
  if (
    annotation.shape === "path" ||
    annotation.shape === "polyline" ||
    step.transition?.style === "instant" ||
    prefersReducedMotion()
  )
    return 0;
  if (step.transition?.duration_ms !== undefined)
    return step.transition.duration_ms;
  const [stroke] = highlightStrokes(annotation, annotationId).strokes;
  return drawDurationMs(stroke?.points ?? [], step.pace);
}

/** Draw the highlight's sketch ink with the live cursor as its pen. Every
 * frame keeps the mark's shape and geometry and advances only `drawProgress`;
 * the final frame is the finished mark with its callout. */
export function animateHighlight(
  request: PresentCursorRequest & { annotationId: string },
  annotation: PresentAnnotation,
  durationMs: number,
  signal: AbortSignal,
): Promise<boolean> {
  const { box, strokes } = highlightStrokes(annotation, request.annotationId);
  return animatePresentationPath(
    [
      { x: 0, y: 0 },
      { x: 1, y: 1 },
    ],
    durationMs,
    signal,
    ({ progress: drawProgress, frame }) => {
      const done = drawProgress === 1;
      const tip = sketchMarkTip(strokes, drawProgress) ?? { x: 0, y: 0 };
      presentCursor({
        ...request,
        annotation: done
          ? { ...annotation, transition: "instant" }
          : { ...annotation, drawProgress, transition: "instant" },
        annotationCallout: done ? request.annotationCallout : undefined,
        cursorPoint: {
          x: box.left + tip.x,
          y: box.top + tip.y,
          displayId: annotation.displayId,
        },
        actionSeq: (request.actionSeq ?? 0) * 10_000 + frame,
        travelDurationMs: 0,
        completionToken: undefined,
      });
    },
  );
}
